import { useState } from 'react';
import { Star, Clock, GitMerge } from 'lucide-react';
import { useCsat, useWorklogs, useMerge } from '../../hooks/useTicketExtras';
import { useAuth } from '../../hooks/useAuth';
import { hasRole } from '../../utils/roleUtils';
import { classNames } from '../../utils/helpers';
import { Modal } from '../ui';
import Button from '../Button';

const CLOSED = ['Resolved', 'Completed', 'ClosedCompleted', 'Closed', 'ClosedCannotImplement', 'ClosedOutOfScope', 'ClosedDuplicate'];

function Stars({ value, onSelect }: { value: number; onSelect?: (n: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} disabled={!onSelect} onMouseEnter={() => onSelect && setHover(n)} onMouseLeave={() => setHover(0)}
          onClick={() => onSelect?.(n)} className={onSelect ? 'cursor-pointer' : 'cursor-default'}>
          <Star size={22} className={classNames((hover || value) >= n ? 'text-amber-400 fill-amber-400' : 'text-gray-300')} />
        </button>
      ))}
    </div>
  );
}

export default function TicketExtrasPanel({ ticketId, status, requesterId }: {
  ticketId: string; status: string; requesterId?: string;
}) {
  const { user } = useAuth();
  const isRequester = user?.id === requesterId;
  const isIT = hasRole(user, 'it_staff') || hasRole(user, 'it_manager') || hasRole(user, 'admin');
  const isElevated = hasRole(user, 'it_manager') || hasRole(user, 'admin');
  const isClosed = CLOSED.includes(status);

  const { csat, submit } = useCsat(ticketId);
  const { worklogs, totalMinutes, add } = useWorklogs(ticketId, isIT);
  const { merge } = useMerge(ticketId);

  const [comment, setComment] = useState('');
  const [mins, setMins] = useState('');
  const [note, setNote] = useState('');
  const [mergeOpen, setMergeOpen] = useState(false);
  const [target, setTarget] = useState('');

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-5">
      {/* CSAT */}
      {isClosed && (isRequester || csat) && (
        <div>
          <h4 className="text-sm font-semibold text-navy mb-2">Satisfaction</h4>
          {csat ? (
            <div>
              <Stars value={csat.rating} />
              {csat.comment && <p className="text-xs text-gray-500 mt-1 italic">“{csat.comment}”</p>}
            </div>
          ) : isRequester ? (
            <div className="space-y-2">
              <Stars value={0} onSelect={n => submit(n, comment)} />
              <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Optional comment…"
                className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
              <p className="text-xs text-gray-400">Click a star to rate how this request was handled.</p>
            </div>
          ) : null}
        </div>
      )}

      {/* Worklogs */}
      {isIT && (
        <div>
          <h4 className="flex items-center gap-1.5 text-sm font-semibold text-navy mb-2">
            <Clock size={14} /> Time logged {totalMinutes > 0 && <span className="text-gray-400">({(totalMinutes / 60).toFixed(1)}h)</span>}
          </h4>
          <div className="flex items-center gap-2 mb-2">
            <input type="number" min={1} value={mins} onChange={e => setMins(e.target.value)} placeholder="Minutes"
              className="w-24 text-sm rounded-lg border border-gray-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand/30" />
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="What did you do?"
              className="flex-1 text-sm rounded-lg border border-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand/30" />
            <Button size="sm" onClick={() => { const m = Number(mins); if (m > 0) { add(m, note); setMins(''); setNote(''); } }}>Log</Button>
          </div>
          {worklogs.length > 0 && (
            <ul className="space-y-1">
              {worklogs.map(w => (
                <li key={w.id} className="flex items-center justify-between text-xs text-gray-500 border-b border-gray-50 py-1">
                  <span>{w.note || 'Work logged'}</span>
                  <span className="font-medium text-navy">{w.minutes}m</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Merge */}
      {isElevated && (
        <div>
          <Button size="sm" variant="ghost" onClick={() => setMergeOpen(true)}><GitMerge size={14} /> Merge as duplicate</Button>
        </div>
      )}

      {mergeOpen && (
        <Modal open onClose={() => setMergeOpen(false)} size="sm" title="Merge ticket"
          footer={<><Button variant="ghost" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button variant="danger" disabled={!target} onClick={async () => { if (await merge(target.trim())) { setMergeOpen(false); window.location.reload(); } }}>Merge</Button></>}>
          <p className="text-sm text-gray-600 mb-2">This ticket will be closed as a duplicate and linked to the target ticket.</p>
          <label className="block text-xs font-medium text-gray-600 mb-1">Target ticket reference</label>
          <input value={target} onChange={e => setTarget(e.target.value)} placeholder="e.g. PSH-IT-SRF-00021-…"
            className="w-full text-sm font-mono rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
        </Modal>
      )}
    </div>
  );
}
